<template>
  <div class="block multi-domain">
    <div class="heading">
      <h2><v-icon class="sr-icon-check"/>&nbsp;{{$t('bulk.domainsAdded', {domains: validDomainCount})}}</h2>
      <div :class="menu ? 'bulk active' : 'bulk'">
        <v-menu
            v-model="menu"
            :close-on-content-click="false"
            attach=""
            ref="bulkMenu"
            :content-class="'bulk-menu-content'"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
                outlined
                v-bind="attrs"
                v-on="on"
                class="bulk-btn"
                :ripple="false"
            >
              <v-icon class="sr-icon-settings icon-before"/>
              {{ $t('bulk.actions') }}
              <v-icon class="icon-after arrow_drop_down"/>
            </v-btn>
          </template>

          <v-card class="bulk-dropdown">
            <p class="options-heading">
              {{ $t('bulk.selectedDomainOptions') }}
            </p>

            <transition name="fade">
              <div v-if="showAppliedInfo" class="applied-info">
                <span class="inner"><i class="sr-icon-check"></i>{{$t('bulk.appliedToDomains', {count: appliedDomains})}}</span>
              </div>
            </transition>

            <h3 v-html="$t('domains.registrationPeriod')"></h3>

            <v-select
                v-model="bulk.registrationPeriod"
                :placeholder="$t('bulk.select')"
                outlined
                ref="reg-period-select"
                :items="bulkOption().registrationPeriod"
                item-text="label"
                item-value="value"
                :noDataText="$t('noDataAvailable')"
                :menu-props="{ bottom: true, offsetY: true }"
                clearable
                persistent-placeholder
                attach
            />

            <h3 v-html="$t('domains.whoisDefender.name')"></h3>

            <v-select
                v-model="bulk.idprotection"
                :placeholder="$t('bulk.select')"
                outlined
                ref="idprotection-select"
                item-text="label"
                item-value="value"
                :items="bulkOption().idprotection"
                :noDataText="$t('noDataAvailable')"
                :menu-props="{ bottom: true, offsetY: true }"
                clearable
                persistent-placeholder
                attach
            />

            <h3 v-html="$t('domains.giftHostMonth')"></h3>

            <v-select
                v-model="bulk.hostingGift"
                :placeholder="$t('bulk.select')"
                outlined
                ref="hosting-gift-select"
                item-text="label"
                item-value="value"
                :items="bulkOption().hostingGift"
                :noDataText="$t('noDataAvailable')"
                :menu-props="{ bottom: true, offsetY: true }"
                clearable
                persistent-placeholder
                attach
            />

            <h3 v-html="$t('bulk.sslPositive')"></h3>

            <v-select
                v-model="bulk.ssl"
                :placeholder="$t('bulk.select')"
                outlined
                ref="ssl-select"
                item-text="label"
                item-value="value"
                :items="bulkOption().ssl"
                :noDataText="$t('noDataAvailable')"
                :menu-props="{ bottom: true, offsetY: true }"
                clearable
                attach
                @focus="updateMenu"
            />
          </v-card>
        </v-menu>
      </div>
    </div>

    <domain-block
        ref="domainBlock"
        :id="index === selectedDomains.length - 1 ? 'lastDomainBlock' : null"
        v-for="(domainObj, index) in selectedDomains"
        :value="domainObj.name"
        :key="domainObj.id"
        :index="index"
        :domain-name="domainObj.name"
        :domain-obj="domainObj"
        @remove-domain="removeDomain"
        :currency-info="currencyInfo"
        :initial-domain-opt="initialDomainOpt"
        :free-domain-info="freeDomainInfo"
        :domain-type="domainType"
        :init-upsell-data="initUpsellData"
        @update-domain="onUpdateDomain"
        :domains-card="domainsCard"
        :bulk="bulk"
    />

    <div class="add-domain" v-if="selectedDomains.length < 30">
      <v-btn text @click="addDomain">
        <i class="sr-icon-plus"></i>{{$t('bulk.addDomain')}}
      </v-btn>
    </div>
  </div>
</template>

<script>
import DomainBlock from "./DomainBlock.vue";

export default {
  name: "MultiDomainContent",
  components: {
    DomainBlock
  },
  props: {
    currencyInfo: { type: [Object, Array], default: () => ({}) },
    initialDomainOpt: { type: String },
    freeDomainInfo: { type: [Object, Array], default: () => ([]) },
    initUpsellData: { type: [Object, Array], default: () => ([]) },
    domainType: { type: String },
  },
  data: () => ({
    domains: [],
    domainsCard: [],
    menu: false,
    selectedPeriod: null,
    selectedSsl: null,
    bulk: {
      registrationPeriod: undefined,
      idprotection: undefined,
      hostingGift: undefined,
      ssl: undefined
    },
    appliedDomains: 0,
    showAppliedInfo: false,
    appliedInfoTimeout: null,
    selectedDomains: [],
    domainBlockRefs: [],
  }),
  computed: {
    validDomainCount() {
      return this.domainsCard?.filter(d => d.domain && d.domain.isValidDomain === true).length;
    }
  },
  created() {
    const raw = localStorage.getItem('bulkDomainCart');
    let bulkFromStorage = [];

    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        bulkFromStorage = parsed;
      }
    }

    const domains = Array.isArray(bulkFromStorage) && bulkFromStorage.length > 0 ? bulkFromStorage : this.domains

    this.selectedDomains = (domains).map((domain, index) => ({
      id: `${Date.now()}-${index}`,
      name: domain
    }));

    localStorage.setItem('bulkDomainCart', JSON.stringify(domains));
  },
  methods: {
    bulkOption() {
      const options = {
        registrationPeriod: Array.from({ length: 10 }, (_, i) => {
          const year = i + 1;
          return {
            value: year,
            label: `${year} ${this.$tc('year', year)}`
          };
        }),
        idprotection: [
          { value: true, label: this.$t('bulk.enableForAll') },
          { value: false, label: this.$t('bulk.disableForAll') }
        ],
        hostingGift: [
          { value: true, label: this.$t('bulk.enableForAll') },
          { value: false, label: this.$t('bulk.disableForAll') }
        ],
        ssl: [
          { value: true, label: this.$t('bulk.enableForEach') },
          { value: false, label: this.$t('bulk.disableForEach') }
        ]
      };

      return options;
    },
    removeDomain(domainObj) {
      this.selectedDomains = this.selectedDomains.filter(domain => domain.id !== domainObj.id);
      this.domainsCard = this.domainsCard.filter(item => item.domain?.name !== domainObj.name);
    },
    addDomain() {
      const last = this.selectedDomains[this.selectedDomains.length - 1];
      if (last && last.name === '') {
        const el = this.$el.querySelector("#lastDomainBlock");
        const target = el?.$el || (el instanceof HTMLElement ? el : null);
        if (target) this.$vuetify.goTo(target, { offset: 20 });
      } else {
        this.selectedDomains.push({
          name: '',
          id: `${Date.now()}-${this.selectedDomains.length}`,
        });
      }
    },
    onUpdateDomain({ updatedDomain }) {
      const domainData = updatedDomain?.domain;
      const isValid = domainData?.isValidDomain;
      const name = domainData?.name;
      const id = domainData?.id;

      if (!isValid || !name || !id) return;

      const selectedIndex = this.selectedDomains.findIndex(d => d.id === id);
      if (selectedIndex === -1) return;

      if (this.selectedDomains[selectedIndex].name !== name) {
        this.$set(this.selectedDomains[selectedIndex], 'name', name);
      }

      while (this.domainsCard.length <= selectedIndex) {
        this.domainsCard.push({});
      }

      this.$set(this.domainsCard, selectedIndex, updatedDomain);
    },
    updateMenu(changedField) {
      this.$nextTick(() => {
        if (this.$refs.bulkMenu && this.$refs.bulkMenu.updateDimensions) {
          this.$refs.bulkMenu.updateDimensions();
          this.calculateAppliedDomains(changedField);
        }
      });
    },
    calculateAppliedDomains(changedField) {
      this.showAppliedInfo = false;

      const bulkValue = this.bulk[changedField];
      if (bulkValue === undefined) {
        this.appliedDomains = 0;
        return;
      }

      const matched = this.domainsCard.filter(d => {
        if (!d.domain?.isValidDomain) return false;

        let domainValue;
        if (changedField === 'registrationPeriod') {
          domainValue = Number(d.domain?.year);
        } else {
          domainValue = d[changedField]?.value;
        }

        return domainValue === bulkValue;
      });

      this.appliedDomains = matched.length;

      if (this.appliedDomains > 0) {
        this.showAppliedInfo = true;
        clearTimeout(this.appliedInfoTimeout);
        this.appliedInfoTimeout = setTimeout(() => {
          this.showAppliedInfo = false;
        }, 1500);
      }
    },
    validateAllDomains() {
      const domainRefs = this.$refs.domainBlock;
      const refsArray = Array.isArray(domainRefs) ? domainRefs : [domainRefs];
      return refsArray.every(ref => ref?.validateDomainName());
    }
  },
  watch: {
    'bulk.registrationPeriod': function () {
      this.updateMenu('registrationPeriod');
    },
    'bulk.idprotection': function () {
      this.updateMenu('idprotection');
    },
    'bulk.hostingGift': function () {
      this.updateMenu('hostingGift');
    },
    'bulk.ssl': function () {
      this.updateMenu('ssl');
    },
    "domainsCard": {
      handler(domains) {
        this.$emit('update-domains-card', domains);
      },
      immediate: true,
      deep: true
    },

  },
}
</script>

<style lang="scss">
  .multi-domain {

    .heading {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      justify-content: space-between;
      padding-bottom: 20px;
      gap: 20px;
    }

    h2 {
      margin: 0;

      .v-icon {
        color: $green;
        font-size: 15px;
        margin-right: 3px;
        width: 19px;
      }
    }

    .add-domain {

      .v-btn {
        color: $gray;
        font-size: 16px !important;
        font-weight: 400;
        padding-left: 10px !important;
        padding-right: 10px !important;
      }

      i {
        font-size: 14px;
        margin-right: 2px;
      }
    }

    .bulk {

      &.active {
        .bulk-btn {
          border-color: $primary;

          &:before {
            border-color: $primary;
            opacity: 1;
          }

          .v-icon {
            color: $primary;

            &.icon-after {
              transform: rotate(180deg);
            }
          }
        }
      }
    }

    .v-btn.bulk-btn {
      text-transform: none;
      min-width: 0;
      justify-content: center;
      align-items: center;
      font-size: 16px !important;
      padding: 5px 10px !important;
      font-weight: 400;
      transition: $transition;
      border-radius: 3px;
      border-color: rgba($black, 0.38);

      &:before {
        background-color: transparent !important;
        border: 1px solid transparent;
      }

      &:hover {
        border-color: $black;
      }

      .v-icon {

        &.icon-before {
          font-size: 16px;
          margin-right: 5px;
        }

        &.icon-after {
          font-size: 27px;
          margin-left: 6px;
        }
      }
    }

    .applied-info {
      position: relative;
      z-index: 2;

      .inner {
        padding: 6px 3px;
        background-color: rgba($primary, .2);
        color: $primary;
        position: absolute;
        top: 4px;
        left: 0;
        right: 0;
        font-size: 16px;
        border-radius: 4px;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;

        &:before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          background-color: $white;
          z-index: -1;
        }

        i {
          font-size: 13px;
          margin-right: 2px;
        }
      }
    }
  }

  .bulk-menu-content {
    border: 2px solid rgba($black, .12);
    box-shadow: 0px 3px 10px 0px rgba($black, 0.2);
    border-radius: 4px;

    overflow: visible !important;
    max-height: none !important;
    clip: auto !important;
    contain: none !important;
    z-index: 9999;
  }

  .bulk-dropdown {
    max-width: 330px;
    padding: 20px;

    overflow: visible !important;
    max-height: none !important;
    clip: auto !important;
    contain: none !important;

    .options-heading {
      font-weight: 400;
      font-size: 16px;
      margin-bottom: 0;
    }

    h3 {
      margin: 12px 0 9px;
      font-size: 16px;

      &:not(:first-of-type) {
        border-top: 1px solid #CCC;
        padding-top: 12px;
      }
    }

    .theme--light.v-input.v-select {

      &, .v-label {
        font-size: 16px;
      }

      .v-label {
        top: 10px;
      }

      .v-input__slot {
        min-height: 40px !important;
      }

      .v-select__selection {
        max-width: 95%;
      }

      .v-input__append-inner {
        margin-top: 8px;

        .v-input__icon--append .v-icon__svg {
          height: 24px;
          width: 24px;
        }
      }

      input[type="text"][readonly="readonly"] {
        display: block;
        padding: 0;
        height: auto;
      }
    }

    .v-list-item {
      min-height: 44px;
      padding: 0 10px;

      .v-list-item__title {
        font-size: 16px;
      }
    }
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.5s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
</style>
